import { BaseURL, version } from "../base/env";

import { stringifyUrl } from "query-string";
import { gql } from "@apollo/client";
import store from "../store";

import { client } from "../services/api/graphql/initialize";
import { set_user_token } from "../store/actions/login_actions";
import i18n from "../i18n";

const TOKEN_REFRESH_MUTATION = gql`
  query me {
    me {
      id
    }
  }
`;

const getToken = () => {
  const token = store.getState().Login.token;
  if (token) {
    return { Authorization: `Bearer ${token}` };
  } else {
    return {};
  }
};

const getFormHeaders = () => {
  return {
    Accept: "application/json",
    ...getToken(),
    "app-version": version,
  };
};

const getHeaders = () => {
  return {
    Accept: "application/json",
    ...getToken(),
    "Content-Type": "application/json",
    "app-version": version,
    "Accept-Language": i18n.language,
  };
};

const getRequestUrl = (url, query, refetch = false) => {
  if (refetch) {
    query["_"] = Date.now();
  }

  return stringifyUrl(
    { url, query },
    {
      skipNull: true,
      skipEmptyString: true,
      encode: false,
    }
  );
};

const getFormRequestPayload = (method, body) => {
  return {
    method,
    timeout: 50000,
    headers: getFormHeaders(),
    body: method === "get" ? null : body,
  };
};

const getRequestPayload = (method, body) => {
  return {
    method,
    timeout: 50000,
    headers: getHeaders(),
    body: method === "get" ? null : JSON.stringify(body),
  };
};

const rawRequest = (method, url, body = {}, query = {}) => {
  return new Promise((resolve, reject) => {
    fetch(getRequestUrl(url, query), getRequestPayload(method, body))
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const formRequest = (method, url, body = {}, query = {}) => {
  return new Promise((resolve, reject) => {
    fetch(getRequestUrl(url, query), getFormRequestPayload(method, body))
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const request = async (method, url, body = {}, query = {}, refetch = false) => {
  const performRequest = async (retry = false) => {
    const response = await fetch(
      getRequestUrl(url, query, refetch),
      getRequestPayload(method, body)
    );

    if (response.status === 401 && !retry) {
      const { data } = await client.mutate({
        mutation: TOKEN_REFRESH_MUTATION,
        variables: {},
      });

      return performRequest(true);
    }

    return response;
  };

  try {
    const response = await performRequest();
    const jsonResponse = await response.json();
    return jsonResponse;
  } catch (error) {
    throw error;
  }
};

const formGet = (url, query = {}) => {
  return formRequest("get", url, null, query);
};

const formPost = (url, body = {}, query = {}) => {
  return formRequest("post", url, body, query);
};

const formPut = (url, body = {}, query = {}) => {
  return formRequest("put", url, body, query);
};

const formDel = (url, body = {}, query = {}) => {
  return formRequest("delete", url, body, query);
};

const rawGet = (url, query = {}) => {
  return rawRequest("get", url, null, query);
};

const rawPost = (url, body = {}, query = {}) => {
  return rawRequest("post", url, body, query);
};

const rawPut = (url, body = {}, query = {}) => {
  return rawRequest("put", url, body, query);
};

const rawDel = (url, body = {}, query = {}) => {
  return rawRequest("delete", url, body, query);
};

const get = (url, query = {}, refetch = false) => {
  return request("get", url, null, query, refetch);
};

const post = (url, body = {}, query = {}, refetch = false) => {
  return request("post", url, body, query, refetch);
};

const put = (url, body = {}, query = {}, refetch = false) => {
  return request("put", url, body, query, refetch);
};

const del = (url, body = {}, query = {}, refetch = false) => {
  return request("delete", url, body, query, refetch);
};

export const Request = {
  get,
  post,
  put,
  del,
  rawGet,
  rawPost,
  rawPut,
  rawDel,
  formGet,
  formPost,
  formPut,
  formDel,
};

export const getUrl = (url) => {
  return `${BaseURL}${url}`;
};
